import { infuse } from '../ioc-config';
import { Logger } from './logger.util';

/**
 * Returns a string identifier that can be used to prefix keys in the local
 * storage.
 */
export const generatePrefix = (includeLang = false): string => {
  const { siteId, lang, apiMode } = infuse('config');

  return includeLang ? `${siteId}-${lang}-${apiMode}` : `${siteId}-${apiMode}`;
};

// Old prefix required for checkout integration!
// Checkout app is reading the cartId from a cookie with the old prefix
// Will be removed once the new prefix is aligned with the Checkout team
export const generatePrefixOld = (): string => {
  const { siteId, apiMode } = infuse('config');
  const suffix = apiMode.startsWith('dev') ? '-dev' : '';

  return `${siteId}${suffix}`;
};

const getKey = (key: string, includeLang = false) => `${generatePrefix(includeLang)}-${key}`.toLowerCase();

export const removeStorageItem = (key: string, includeLang = false): void => {
  try {
    localStorage.removeItem(getKey(key, includeLang));
    sessionStorage.removeItem(getKey(key, includeLang));
  } catch {
    Logger.error(
      `PersistenceUtil::removeStorageItem() -> Failed to remove item "${getKey(key, includeLang)}" from storage`,
    );
  }
};

export const setStorageItem = <T>(key: string, value?: T, includeLang = false): void => {
  try {
    const storage = sessionStorage;

    if (value === undefined) {
      storage.removeItem(getKey(key, includeLang));
    } else {
      storage.setItem(getKey(key, includeLang), typeof value !== 'string' ? JSON.stringify(value) : value);
    }
  } catch (err) {
    Logger.error(
      `PersistenceUtil::setStorageItem() -> Failed to save item "${getKey(key, includeLang)}" into storage`,
      err,
    );
  }
};

export const getStorageItem = <T>(key: string, defaultValue?: T, includeLang = false): T => {
  const item = sessionStorage.getItem(getKey(key, includeLang)) ?? localStorage.getItem(getKey(key, includeLang));

  try {
    return typeof item === 'string' ? JSON.parse(item) : defaultValue;
  } catch {
    if (item !== undefined) {
      return item as unknown as T;
    }

    Logger.error(`PersistenceUtil::getStorageItem() -> Storage item "${getKey(key)}" not available`);

    return defaultValue as T;
  }
};

const cookieDomainHelper = () => {
  //  Get main domain from possible subdomain
  const domain = window.location.hostname.match(/[\w-]+(\.com{0,1}){0,1}\.[a-zA-Z]+$/)?.[0];

  return domain ? `; domain=.${domain}` : '';
};

/**
 * Helper function to remove a Cookie item.
 *
 * @param {string} key
 * @param prefix
 */
export const removeCookieItem = (key, prefix = generatePrefix()) => {
  // delete Cookie by updating its expiration time to a past date.
  document.cookie = `${prefix}-${key}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${cookieDomainHelper()}; Secure;`;
};

/**
 * Helper function to set an item to Cookies.
 *
 * @param {string} key
 * @param {(string|Object)} value
 * @param {number} duration in milliseconds
 * @param prefix
 */
export const setCookieItem = (key, value, duration = 0, prefix = generatePrefix()) => {
  if (value instanceof Object) {
    value = JSON.stringify(value);
  }

  const itemKey = `${prefix}-${key}`;

  if (value === undefined) {
    removeCookieItem(key);
  } else {
    let optionalExpirationDateString = '';

    if (duration && !Number.isNaN(parseInt(duration.toString(), 10))) {
      // expiration date = current date + given duration in milliseconds
      optionalExpirationDateString = `; expires=${new Date(Date.now() + duration).toUTCString()}`;
    }

    //  Get main domain from possible subdomain
    const optionalDomainString = cookieDomainHelper();

    // Dont use Secure flag on local environment, otherwise cookie will not be saved
    if (window.location.protocol.includes('http:')) {
      document.cookie = `${itemKey}=${value}${optionalExpirationDateString}${optionalDomainString}; path=/;`;
    } else {
      document.cookie = `${itemKey}=${value}${optionalExpirationDateString}${optionalDomainString}; path=/; Secure;`;
    }
  }
};

/**
 * Helper function to get an item from Cookies.
 *
 * @param {string} key
 * @param prefix
 * @returns {(string|Object)}
 */
export const getCookieItem = (key, prefix = generatePrefix()) => {
  const item = ((document.cookie || '').split('; ').find((row) => row.startsWith(`${prefix}-${key}=`)) || '').split(
    '=',
  )[1];

  try {
    return JSON.parse(item);
  } catch (e) {
    return item;
  }
};
